import { DataAggregationFunction, DataFieldDto, DataFieldReference, DataFieldSource, DataFieldSourceType, DataFieldType, DataFieldUsage, DataFieldUsageHints, DataSortMode, TableColumnDataFieldSource, ValueDataType } from 'src/app/services/api.services';
import { deepClone } from 'src/app/shared/clone/cloner-service';
import { DataModel } from './DataModel';
import { DataTable } from './DataTable';
import { DataFormat } from './DataFormat';
import { DataDisplayOptions } from './DataDisplayOptions';
import { DataType } from './DataType';
import { DataAggregation } from './DataAggregation';
import { INamedElement, NamedElementHierarchyCollection } from './Collections';
import { mapToRecord } from '../utils/mapToRecord';

export class DataField implements INamedElement {
  readonly children = new DataField.ChildCollection(this);

  constructor(
    public readonly table: DataTable,
    public readonly name: string,
    public readonly parent?: DataField) { }

  get model() { return this.source.model; }
  get source() { return this.table.source; }

  readonly reference: DataFieldReference = {
    table: this.table.reference,
    path: [...(this.parent?.reference.path ?? []), this.name]
  };

  type: DataFieldType = DataFieldType.Model;

  get isModel() { return this.type == DataFieldType.Model; }
  get isCurrencyVariant() { return this.type == DataFieldType.CurrencyVariant; }
  get isCustomField() { return this.type == DataFieldType.CustomField; }
  get isUDF() { return this.type == DataFieldType.UDF; }

  sourceKey: any = '';

  valueSource: DataFieldSource = <TableColumnDataFieldSource>{
    type: DataFieldSourceType.TableColumn,
    column: { table: '', column: '' }
  };

  indexed: boolean = false;

  hasErrors: boolean = false;
  removed: boolean = false;

  dataType: DataType = DataType.Text;
  isNullable: boolean = false;

  get isBoolean() { return this.dataType.isBoolean; }

  get isDecimal() { return this.dataType.isDecimal; }
  get isInteger() { return this.dataType.isInteger; }
  get isNumber() { return this.dataType.isNumber; }

  get isNumeric() { return this.isDecimal || this.isInteger || this.isNumber; }

  get isDateOnly() { return this.dataType.isDateOnly; }
  get isTimeOnly() { return this.dataType.isTimeOnly; }
  get isDateTime() { return this.dataType.isDateTime; }

  get isDateOrDateTime() { return this.isDateOnly || this.isDateTime; }

  get isText() { return this.dataType.isText; }

  get isList() { return this.dataType.isList; }
  get isMap() { return this.dataType.isMap; }

  format: DataFormat | null;

  get effectiveFormat() {
    return this.format ?? this.table.source.defaultFormats.get(this.dataType.value) ?? DataFormat.defaultValue;
  }

  isValueAggregated: boolean = false;


  sortByValueSource: DataFieldSource | null;
  defaultSort: DataSortMode = DataSortMode.Unset;

  defaultUsage: DataFieldUsage | null = null;

  get effectiveDefaultUsage() {
    return this.defaultUsage ?? (
      (this.isValueAggregated || this.effectiveDefaultAggregation)
        ? DataFieldUsage.Measure
        : DataFieldUsage.Grouping
    );
  }


  defaultAggregation: DataAggregation | null = null;

  get effectiveDefaultAggregation() {
    return this.isValueAggregated
      ? null
      : this.defaultAggregation ?? (this.effectiveMustHaveAggregation ? this.dataType.defaultAggregation : null);
  }


  mustHaveAggregation: boolean | null = false;

  get effectiveMustHaveAggregation() {
    return this.mustHaveAggregation ?? false;
  }


  displayOptions: DataDisplayOptions | null;

  get effectiveDisplayOptions() {
    return this.displayOptions
      ?? this.table.source.defaultDisplayOptions.get(this.dataType.value)
      ?? DataDisplayOptions.defaults;
  }


  usageHints: DataFieldUsageHints | null;


  readonly currencyVariants = new Map<string, DataField>();

  readonly additionalProperties = new Map<string, any>();


  setFromDto1(dto: DataFieldDto) {
    this.type = dto.type;

    this.sourceKey = deepClone(dto.sourceKey);

    this.valueSource = deepClone(dto.valueSource);
    this.indexed = dto.indexed;

    this.hasErrors = dto.hasErrors;
    this.removed = dto.removed;

    this.dataType = DataType.from(dto.dataType);
    this.isNullable = dto.isNullable;

    this.format = deepClone(dto.format);
    this.isValueAggregated = dto.isValueAggregated;
    
    this.sortByValueSource = deepClone(dto.sortByValueSource);
    this.defaultSort = dto.defaultSort;
    this.defaultUsage = dto.defaultUsage;
    this.defaultAggregation = dto.defaultAggregation ? DataAggregation.from(dto.defaultAggregation) : null;

    this.mustHaveAggregation = dto.mustHaveAggregation;

    this.displayOptions = deepClone(dto.displayOptions);

    this.usageHints = deepClone(dto.usageHints);

    for (let [k, v] of Object.entries(dto.additionalProperties)) {
      this.additionalProperties.set(k, deepClone(v));
    }

    dto.children?.forEach(x => this.children.add(x.name).setFromDto1(x));
  }

  setFromDto2(dto: DataFieldDto) {
    dto.children?.forEach(x => this.children.get(x.name).setFromDto2(x));

    for (let [k, v] of Object.entries(dto.currencyVariants))
      this.currencyVariants.set(k, this.model.fields.get(v));
  }


  getEditorIcon() {
    if (this.isCustomField) {
      return 'fa-calculator';
    }

    if (this.effectiveDefaultUsage == DataFieldUsage.Measure) {
      return 'fas fa-ruler-combined';
    }

    if (this.parent && this.parent.isDateOrDateTime) {
      return 'fas fa-calendar-alt';
    }

    return 'fas fa-layer-group';
  }

  toDto(): DataFieldDto {
    return {
      name: this.name,
      type: this.type,
      
      sourceKey: deepClone(this.sourceKey),
      
      valueSource: deepClone(this.valueSource),
      indexed: this.indexed,

      hasErrors: this.hasErrors,
      removed: this.removed,

      dataType: this.dataType.value,
      isNullable: this.isNullable,

      format: deepClone(this.format),
      isValueAggregated: this.isValueAggregated,

      sortByValueSource: deepClone(this.sortByValueSource),
      defaultSort: this.defaultSort,
      defaultUsage: this.defaultUsage,
      defaultAggregation: this.defaultAggregation?.value,

      mustHaveAggregation: this.mustHaveAggregation,

      displayOptions: deepClone(this.displayOptions),

      usageHints: deepClone(this.usageHints),

      children: [...this.children.map(x => x.toDto())],

      currencyVariants: mapToRecord(this.currencyVariants),

      additionalProperties: mapToRecord(this.additionalProperties),
    };
  }
}

export namespace DataField {
  export class ChildCollection extends NamedElementHierarchyCollection<DataField> {
    constructor(public readonly f: DataField) {
      super();
    }

    add(name: string) {
      let item = new DataField(this.f.table, name, this.f);
      this._add(item);
      return item;
    }

    getOrAdd(name: string) {
      return this.find(name) ?? this.add(name);
    }

    protected findChild(item: DataField, name: string) {
      return item.children.find(name);
    }

    protected getBase() { return this.f.model.base?.fields.find(this.f.reference)?.children; }
    protected getExtensions() { return this.f.model.base?.extensions.map(x => x.fields.find(this.f.reference)?.children) ?? []; }
  }
}
